import React, { useState, useEffect, useMemo } from "react";
import AdminMainLayout from "../AdminMainLayout";
import {
  Row,
  Col,
  Typography,
  Card,
  Divider,
  Button,
  Space,
  Tooltip,
  Modal,
  Spin,
  Select
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';
import DottedLoader from "../../DottedLoader";
import styles from "./AdminThroughputCharts.module.css";
import CompanyChart from "./CompanyChart";
import QAChart from "./QAChart";
import CycleTimeChart from "./CycleTimeChart";
import ThroughputDetails from "./ThroughputDetails";
import PresetRangeSelector from "./PresetRangeSelector";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import { getPresetDateRange } from "./chartUtils";
import { filterRecordsByPattern } from "../../Utilities";

const { Title } = Typography;
const { Option } = Select;

const DATE_FORMAT = "YYYY/MM/DD";

const COMPANY_NAME_FILTERS = [
  "Google Signup",
  "%test%",
  "%tintash%",
  "NA"
];

const AdminThroughputCharts = () => {
  const chartStyle = useMemo(() => ({ 
    padding: 20, 
    background: "#fff", 
    borderRadius: 8 
  }), []);

  const [companyList, setCompanyList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  const [masterDateRange, setMasterDateRange] = useState({
    start: moment().startOf('month'),
    end: moment()
  });
  const [masterPresetRange, setMasterPresetRange] = useState(undefined);
  const [masterCompanyFilter, setMasterCompanyFilter] = useState(undefined);

  // Initialize filters from URL query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Restore company filter
    const companyFilter = urlParams.get('tp_company');
    if (companyFilter) {
      setMasterCompanyFilter(companyFilter);
    }
    
    // Restore date range
    const startDate = urlParams.get('tp_start');
    const endDate = urlParams.get('tp_end');
    if (startDate && endDate) {
      setMasterDateRange({
        start: moment(startDate, DATE_FORMAT),
        end: moment(endDate, DATE_FORMAT)
      });
    }
    
    // Restore preset range
    const presetRange = urlParams.get('tp_preset');
    if (presetRange) {
      setMasterPresetRange(presetRange);
    }
    
    // Mark initialization as complete
    setIsInitializing(false);
  }, []);

  // Update URL query parameters when filters change
  const updateQueryParams = (newParams) => {
    const urlParams = new URLSearchParams(window.location.search);
    
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        urlParams.set(key, value);
      } else {
        urlParams.delete(key);
      }
    });
    
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  const handleMasterDateChange = (dateValues) => {
    if (dateValues && dateValues[0] && dateValues[1]) {
      const newDateRange = {
        start: dateValues[0],
        end: dateValues[1]
      };
      setMasterDateRange(newDateRange);
      setMasterPresetRange(undefined);
      
      // Update URL with new date range
      updateQueryParams({
        tp_start: dateValues[0].format(DATE_FORMAT),
        tp_end: dateValues[1].format(DATE_FORMAT),
        tp_preset: undefined
      });
    }
  };

  const handleMasterPresetChange = (value) => {
    setMasterPresetRange(value);
    if (value) {
      const dateRange = getPresetDateRange(value);
      if (dateRange) {
        const newDateRange = {
          start: dateRange.start,
          end: dateRange.end
        };
        setMasterDateRange(newDateRange);
        
        // Update URL with new preset range and date range
        updateQueryParams({
          tp_preset: value,
          tp_start: dateRange.start.format(DATE_FORMAT),
          tp_end: dateRange.end.format(DATE_FORMAT)
        });
      }
    } else {
      const defaultDateRange = {
        start: moment().startOf('month'),
        end: moment()
      };
      setMasterDateRange(defaultDateRange);
      
      // Update URL to remove preset range and set default dates
      updateQueryParams({
        tp_preset: undefined,
        tp_start: defaultDateRange.start.format(DATE_FORMAT),
        tp_end: defaultDateRange.end.format(DATE_FORMAT)
      });
    }
  };

  const handleMasterCompanyFilterChange = (value) => {
    setMasterCompanyFilter(value);
    
    // Update URL with new company filter
    updateQueryParams({
      tp_company: value
    });
  };

  useEffect(() => {
    const getCompanyList = async () => {
        try{
            setIsLoading(true);
            const res = await axios.post(ENVIRONMENT.COMPANY_GET_BATCH, {});
            const filteredCompanies = filterRecordsByPattern(res.data, 'company_display_name', COMPANY_NAME_FILTERS);
            filteredCompanies.sort((a, b) =>
            a.company_display_name.localeCompare(b.company_display_name)
            );
          
          const companyList = filteredCompanies.map(company => {
            return {
              companyID: company.company_id,
              companyName: company.company_display_name,
            }
          })

          setCompanyList(companyList);

        }catch (error) {
          console.error("Error fetching company list:", error);
        }finally{
            setIsLoading(false);
        }
    }

    getCompanyList();
  }, []);

  return (
      <Row gutter={[24, 24]}>
        {/* Clean Action-Oriented Header */}
        <Col span={24}>
          <Card style={{ ...chartStyle, marginBottom: 0 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Throughput Charts
                </Title>
              </Col>
              
              <Col style={{ marginTop: 20}}>
                <Space direction="vertical" size={4}>
                  <Space>
                    <Select
                      className='analytics-filter-centered-search-select'
                      style={{ width: 180 }}
                      value={masterCompanyFilter}
                      onChange={handleMasterCompanyFilterChange}
                      placeholder="Select Company"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option.props.children.toLowerCase().includes(input.trim().toLowerCase())
                      }
                    >
                      <Option value="ALL">All Companies</Option>
                      {companyList.map(company => (
                        <Option key={company.companyID} value={company.companyID}>
                          {company.companyName}
                        </Option>
                      ))}
                    </Select>
                    <DateRangePicker
                      start={masterDateRange.start}
                      end={masterDateRange.end}
                      date_format={DATE_FORMAT}
                      onCalChange={handleMasterDateChange}
                    />
                    <PresetRangeSelector
                      value={masterPresetRange}
                      onChange={handleMasterPresetChange}
                      style={{ width: 200 }}
                    />
                    <Tooltip title="All charts below use the same date range and company filter settings.">
                      <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                  <div style={{ fontSize: '11px', color: '#8c8c8c', textAlign: 'right' }}>
                    Data is calculated in UTC time.
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {isLoading && <div className={styles.backdrop}>
            <DottedLoader />
        </div>}
        
        {!isInitializing && (
          <>
            <ThroughputDetails 
              chartStyle={chartStyle} 
              companyList={companyList} 
              masterDateRange={masterDateRange}
              masterPresetRange={masterPresetRange}
              masterCompanyFilter={masterCompanyFilter}
            />

            <CycleTimeChart
              chartStyle={chartStyle} 
              companyList={companyList} 
              setIsLoading={setIsLoading}
              masterDateRange={masterDateRange}
              masterPresetRange={masterPresetRange}
              masterCompanyFilter={masterCompanyFilter}
            />
            
            <CompanyChart 
              chartStyle={chartStyle} 
              companyList={companyList} 
              setIsLoading={setIsLoading}
              masterDateRange={masterDateRange}
              masterPresetRange={masterPresetRange}
              masterCompanyFilter={masterCompanyFilter}
            />

            <QAChart 
              chartStyle={chartStyle} 
              companyList={companyList}
              setIsLoading={setIsLoading}
            />
          </>
        )}
      </Row>
  );
};

export default AdminThroughputCharts;
